// src/utils/transliterate.ts
// Latinica → Ćirilica transliteracija za mk i sr-Cyrl
// Koristi se SAMO za vlastite imenice (imena, adresa, email vizual)
// Glossary: "SUTON" i "Radio SUTON" se NE transliteriraju.

const GLOSSARY_PRESERVE = ['SUTON', 'Radio SUTON'];

// Digrafi (dva karaktera → jedan ćirilični) — moraju ići PRIJE single char mapa
const DIGRAPHS: Record<string, string> = {
  Lj: 'Љ', lj: 'љ',
  Nj: 'Њ', nj: 'њ',
  'Dž': 'Џ', 'dž': 'џ',
  'DŽ': 'Џ',
};

// Single character mapa (sr-Cyrl + dijelimo s mk uz override)
const CHAR_MAP: Record<string, string> = {
  A: 'А', a: 'а',
  B: 'Б', b: 'б',
  V: 'В', v: 'в',
  G: 'Г', g: 'г',
  D: 'Д', d: 'д',
  Đ: 'Ђ', đ: 'ђ',
  E: 'Е', e: 'е',
  Ž: 'Ж', ž: 'ж',
  Z: 'З', z: 'з',
  I: 'И', i: 'и',
  J: 'Ј', j: 'ј',
  K: 'К', k: 'к',
  L: 'Л', l: 'л',
  M: 'М', m: 'м',
  N: 'Н', n: 'н',
  O: 'О', o: 'о',
  P: 'П', p: 'п',
  R: 'Р', r: 'р',
  S: 'С', s: 'с',
  T: 'Т', t: 'т',
  Ć: 'Ћ', ć: 'ћ',
  U: 'У', u: 'у',
  F: 'Ф', f: 'ф',
  H: 'Х', h: 'х',
  C: 'Ц', c: 'ц',
  Č: 'Ч', č: 'ч',
  Š: 'Ш', š: 'ш',
  // Njemački specifičnosti (Zeilweg, Frankfurt am Main…)
  W: 'В', w: 'в',
  Y: 'Ј', y: 'ј',
  Q: 'К', q: 'к',
  X: 'Кс', x: 'кс',
};

// MK specifičnosti — makedonska ćirilica
const MK_OVERRIDES: Record<string, string> = {
  Đ: 'Ѓ', đ: 'ѓ',
  Ć: 'Ќ', ć: 'ќ',
};

/** Vrati [start, end] segmente teksta koji se NE transliteriraju (glossary). */
function findGlossaryRanges(text: string): Array<[number, number]> {
  const ranges: Array<[number, number]> = [];
  for (const term of GLOSSARY_PRESERVE) {
    let idx = 0;
    while ((idx = text.indexOf(term, idx)) !== -1) {
      ranges.push([idx, idx + term.length]);
      idx += term.length;
    }
  }
  return ranges;
}

function isInGlossaryRange(pos: number, ranges: Array<[number, number]>): boolean {
  return ranges.some(([start, end]) => pos >= start && pos < end);
}

/**
 * Glavna funkcija — transliterira latinicu u ćirilicu.
 */
export function transliterate(text: string | undefined | null, locale: 'mk' | 'sr-Cyrl'): string {
  if (!text) return '';

  const charMap = locale === 'mk' ? { ...CHAR_MAP, ...MK_OVERRIDES } : CHAR_MAP;
  const glossaryRanges = findGlossaryRanges(text);

  let result = '';
  let i = 0;

  while (i < text.length) {
    if (isInGlossaryRange(i, glossaryRanges)) {
      const range = glossaryRanges.find(([start]) => i === start);
      if (range) {
        result += text.slice(range[0], range[1]);
        i = range[1];
        continue;
      }
    }

    const twoChar = text.slice(i, i + 2);
    if (DIGRAPHS[twoChar]) {
      result += DIGRAPHS[twoChar];
      i += 2;
      continue;
    }

    const ch = text[i];
    result += charMap[ch] !== undefined ? charMap[ch] : ch;
    i += 1;
  }

  return result;
}

/** Transliterira samo ako je locale mk ili sr-Cyrl; inače vraća original. */
export function maybeTransliterate(text: string | undefined | null, locale: string): string {
  if (!text) return '';
  if (locale === 'mk' || locale === 'sr-Cyrl') {
    return transliterate(text, locale);
  }
  return text;
}
